import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';

import type { CostToWinRow } from '../api/types';
import { money, percent } from '../utils/format';

import { ChartTooltip } from './ChartTooltip';
import { CHROME, SERIES } from './palette';

/**
 * Two things matter about a price gap and neither alone is enough: how little it
 * costs to close, and how many rivals closing it beats. A scatter shows both at
 * once — bottom-right is "a few cents takes rank 1 against a crowded listing".
 *
 * One series, so the all-pairs colour constraint does not apply.
 */
export function CostToWinScatter({ rows }: { rows: CostToWinRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="state">
        <div>Nothing to win right now.</div>
        <div className="muted">Every scanned product is already the cheapest, or has no competitors.</div>
      </div>
    );
  }

  const points = rows.map((row) => ({
    ...row,
    x: row.costToWinEur,
    y: row.offerCount,
  }));

  return (
    <>
      <ResponsiveContainer width="100%" height={300}>
        <ScatterChart margin={{ top: 8, right: 16, bottom: 24, left: 0 }}>
          <CartesianGrid stroke={CHROME.grid} />
          <XAxis
            type="number"
            dataKey="x"
            name="Cost to win"
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={{ stroke: CHROME.axis }}
            tickLine={false}
            tickFormatter={(value: number) => `${value.toFixed(2)} €`}
            label={{
              value: 'euros to cut to reach rank 1  →  cheaper wins on the left',
              position: 'insideBottom',
              offset: -14,
              fill: CHROME.inkMuted,
              fontSize: 11,
            }}
          />
          <YAxis
            type="number"
            dataKey="y"
            name="Competitors"
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={40}
            allowDecimals={false}
            label={{
              value: 'competitors',
              angle: -90,
              position: 'insideLeft',
              fill: CHROME.inkMuted,
              fontSize: 11,
            }}
          />
          <ZAxis type="number" dataKey="ourPrice" range={[60, 260]} />
          <Tooltip
            cursor={{ stroke: CHROME.inkMuted, strokeWidth: 1 }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const row = payload[0].payload as CostToWinRow;

              return (
                <ChartTooltip
                  title={row.name}
                  rows={[
                    { key: 'cut', label: 'Cut needed', value: money(row.costToWinEur), color: SERIES[0] },
                    { key: 'pct', label: 'As share of price', value: percent(row.costToWinPct) },
                    { key: 'our', label: 'Your price', value: money(row.ourPrice) },
                    { key: 'min', label: 'Cheapest offer', value: money(row.minPrice) },
                    { key: 'shop', label: 'Held by', value: row.cheapestShop ?? '—' },
                    { key: 'rank', label: 'Your rank', value: `#${row.ourRank} of ${row.offerCount}` },
                  ]}
                />
              );
            }}
          />
          <Scatter
            isAnimationActive={false}
            data={points}
            fill={SERIES[0]}
            fillOpacity={0.75}
            stroke={CHROME.surface}
            strokeWidth={1}
          />
        </ScatterChart>
      </ResponsiveContainer>
      <div className="legend">
        <span className="legend__item">
          <span className="legend__swatch" style={{ background: SERIES[0] }} />
          One product · bubble size = your current price
        </span>
      </div>
    </>
  );
}
